import React from 'react';
import { reduxForm, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { getLoanApplication, getApplicationDecision, getMarketCode, getProspect } from '../../../selectors';
import { submitFullApp, displayFormErrorOnSubmit, updateFormValue } from '../../../actions/loanApplicationActions';
import ContactFieldSet from '../ContactFieldSet/contactFieldSet';
import LegalFieldSet from '../LegalFieldSet/legalFieldSet';
import LoanPurposeFieldSet from "../LoanPurposeFieldSet/loanPurposeFieldSet";
import NameFieldSet from "../NameFieldSet/nameFieldSet";
import MailingAddressFieldSet from "../AddressFieldSet/mailingAddressFieldSet";
import StreetAddressFieldSet from "../AddressFieldSet/streetAddressFieldSet";
import EmploymentFieldSet from "../EmploymentFieldSet/employmentFieldSet";
import PersonalFieldSet from "../PersonalFieldSet/personalFieldSet";
import PropTypes from 'prop-types';
import { compose } from 'ramda';
import { MarketCodes } from "../../../marketCodes";
import '../applicationForm.scss';

export const getNameFieldsFromProspect = (prospect, marketCode) => {
  const name = prospect.name;
  return marketCode === MarketCodes.DIRECT_MAILER ? {
    firstName: name.firstName,
    middleInitial: name.middleName,
    lastName: name.lastName,
    suffix: name.suffix
  } : {};
};

export const getAddressFieldsFromProspect = (prospect, marketCode) => {
  const address = prospect.address;
  return marketCode === MarketCodes.DIRECT_MAILER ? {
    homeAddress: address.lineOne,
    aptSuiteBldg: address.lineTwo,
    cityTown: address.city,
    state: address.state,
    zip: address.zip
  } : {};
};

export class FullAppForm extends React.Component {

  renderFieldSets = () => {
    const { initialValues, currentValues, updateFormValue } = this.props;
    const updateFullAppFormValue = (key, value) => {
      updateFormValue('fullApp', key, value);
    };

    switch (initialValues.marketCode) {
    case MarketCodes.OPEN_MARKET:
      return [
        <StreetAddressFieldSet key="StreetAddressFieldSet" mailingAddress={initialValues.homeAddress}/>,
        <ContactFieldSet key="ContactFieldSet" displayMiniAppFields={false} />,
        <EmploymentFieldSet key="EmploymentFieldSet" displayMiniAppFields={false} occupation={ initialValues.occupation } updateFormValue={updateFullAppFormValue}/>,
        <PersonalFieldSet key="PersonalFieldSet" displayMiniAppFields={false} isReadOnly={false} isUsCitizen={currentValues.usCitizen} />
      ];
    case MarketCodes.DIRECT_MAILER:
      return [
        <LoanPurposeFieldSet key="LoanPurposeFieldSet" value={currentValues.loanPurpose} />,
        <NameFieldSet key="NameFieldSet" isReadOnly={true} />,
        <MailingAddressFieldSet key="MailingAddressFieldSet" />,
        <StreetAddressFieldSet key="StreetAddressFieldSet" mailingAddress={currentValues.homeAddress || initialValues.homeAddress}/>,
        <ContactFieldSet key="ContactFieldSet" displayMiniAppFields={false} />,
        <EmploymentFieldSet key="EmploymentFieldSet" displayMiniAppFields={false} occupation={ initialValues.occupation } updateFormValue={updateFullAppFormValue}/>,
        <PersonalFieldSet key="PersonalFieldSet" displayMiniAppFields={false} isReadOnly={false} isUsCitizen={currentValues.usCitizen} />
      ];
    case MarketCodes.BANK_MARKET:
      return [
        <NameFieldSet key="NameFieldSet" isReadOnly={false} />,
        <MailingAddressFieldSet key="MailingAddressFieldSet" />,
        <StreetAddressFieldSet key="StreetAddressFieldSet" mailingAddress={currentValues.homeAddress || initialValues.homeAddress}/>,
        <ContactFieldSet key="ContactFieldSet" />,
        <EmploymentFieldSet key="EmploymentFieldSet" displayMiniAppFields={false} occupation={ initialValues.occupation } />,
        <PersonalFieldSet key="PersonalFieldSet" isReadOnly={true} isUsCitizen={currentValues.usCitizen} />
      ];
    default:
      return [];
    }
  };

  render = () => {
    const { handleSubmit, submitFullApp, displayFormErrorOnSubmit, submitting, hasAppDecision } = this.props;

    if (hasAppDecision) {
      return <Redirect push to="/apply/decision"/>;
    }

    return (
      <form autoComplete="off"
        name="fullApp"
        data-track="appstart"
        noValidate="novalidate"
        className="full-app-form application-form mt-4"
        onSubmit={compose(displayFormErrorOnSubmit, handleSubmit(submitFullApp))}>
        {this.renderFieldSets()}
        <LegalFieldSet />
        <p>
          <button type="submit" disabled={submitting}>Continue</button>
        </p>
        <footer>
          <p>
            <i className="material-icons">thumb_up</i> Upon submitting the application your credit information
            will be reviewed which may affect your credit score
          </p>
        </footer>
      </form>);
  };
}

FullAppForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submitFullApp: PropTypes.func.isRequired,
  displayFormErrorOnSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  hasAppDecision: PropTypes.bool
};

const selector = formValueSelector('fullApp');
const mapStateToProps = (state) => {
  const application = getLoanApplication(state);
  const marketCode = getMarketCode(state);
  const prospect = getProspect(state);
  return {
    initialValues: {
      ...application,
      marketCode: marketCode,
      ...getNameFieldsFromProspect(prospect, marketCode),
      ...getAddressFieldsFromProspect(prospect, marketCode)
    },
    currentValues: {
      usCitizen: selector(state, 'usCitizen'),
      loanPurpose: selector(state, 'loanPurpose'),
      homeAddress: selector(state, 'homeAddress')
    },
    hasAppDecision: !!getApplicationDecision(state)
  };
};

const mapDispatchToProps = { submitFullApp, displayFormErrorOnSubmit, updateFormValue };

export const FullAppReduxForm = reduxForm({
  form: 'fullApp'
})(FullAppForm);

export default connect(mapStateToProps, mapDispatchToProps)(FullAppReduxForm);
